import React, { useState } from 'react';
import { translate } from 'components/Translation';
import { BodyS } from 'components/Typography/Typography';
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog';
import { CustomerAccount } from 'utils/api/models/accounts/customerAccountResponse';
import { getProductCharacteristic } from 'views/Numbers/helpers/getters';
import { ProductsService } from 'services/products/products';
import { finalize } from 'rxjs/operators';
import { SIM_DISCONNECTION_PRODUCTS } from 'views/Numbers/constants';
import invalidateCache from 'utils/api/invalidateCache';
import SimpleFailureDialog from 'views/Numbers/Details/common/SimpleFailureDialog';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import OTP from 'components/OTP/OTP';
import { useDispatch } from 'react-redux';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { UsersService } from 'services/users/users';
import { OperationContextEnum } from 'utils/api/payloads/omantelApi';
import { setToast } from 'redux/modules/toast/actions';
import { makeStyles } from '@material-ui/core';
import OtpTab from '../../Settings/subcomponents/ResetAdslPasswordDialog/subcomponents/OtpTab';
import { ERROR_VALIDATING_TOKEN } from 'utils/api/errors/errorCodes';

interface IDisconnectSimCardDialogProps {
	isOpen: boolean;
	hideDialog: () => void;
	isDisconnected: boolean;
	customerAccount: CustomerAccount;
	accountId: string;
	phoneNumber: string;
	fetchCustomerProducts: () => void;
	msisdn: string;
}

const DisconnectSimCardDialog = ({
	isOpen,
	hideDialog,
	isDisconnected,
	customerAccount,
	phoneNumber,
	accountId,
	fetchCustomerProducts,
	msisdn = '',
}: IDisconnectSimCardDialogProps) => {
	const [loading, setLoading] = useState<boolean>(false);
	const [showFailureDialog, setShowFailureDialog] = useState<boolean>(false);
	const [otpValue, setOtpValue] = useState<string>('');
	const [isOTP, setIsOtp] = useState<boolean>(false);
	const [isWrongOtp, setIsWrongOtp] = useState(false);
	const dispatch = useDispatch();

	const hideFailureDialog = () => {
		setShowFailureDialog(false);
	};

	const openFailureDialog = () => {
		setShowFailureDialog(true);
	};

	const close = (): void => {
		hideDialog();
		setIsOtp(false);
		setIsWrongOtp(false);
	};

	const requestForOtp = () => {
		dispatch(showLoadingIndicator());
		setOtpValue('');
		setIsWrongOtp(false);
		UsersService.requestForOtp(OperationContextEnum.CORPCHANGECREDITLIMIT, msisdn)
			.pipe(
				finalize(() => {
					dispatch(hideLoadingIndicator());
					setIsOtp(true);
				})
			)
			.subscribe(() => {
				setToast({
					error: false,
					label: translate('otp.token.sent'),
				});
				setIsOtp(true);
			});
	};

	const disconnectOrDisable = () => {
		let orderItems: types.services.IOrderItem[] = [];
		const action = isDisconnected ? 'REMOVE' : 'ADD';
		SIM_DISCONNECTION_PRODUCTS.forEach((singleProduct) => {
			orderItems.push({
				action,
				...singleProduct,
			});
		});
		setLoading(true);
		const accountExternalId = getProductCharacteristic(customerAccount, 'ACCOUNT_EXTERNAL_ID');
		const subscriberNumber = getProductCharacteristic(customerAccount, 'SUBSCRIBER_NUMBER');
		const accountNumber = getProductCharacteristic(customerAccount, 'ACCOUNT_NO');
		const subscriberNumberResets = getProductCharacteristic(customerAccount, 'SUBSCRIBER_NUMBER_RESETS');
		ProductsService.updateServices({
			phoneNumber,
			accountExternalId,
			subscriberNumber,
			accountNumber,
			subscriberNumberResets,
			accountId: accountId,
			orderItems: orderItems,
			notificationSwap: true,
			otp: otpValue,
		})
			.pipe(finalize(() => setLoading(false)))
			.subscribe(
				({ success }) => {
					close();
					if (success) {
						invalidateCache(['getCustomerProducts'], true);
						fetchCustomerProducts();
					} else {
						openFailureDialog();
					}
				},
				(error) => {
					if (error.response.code === ERROR_VALIDATING_TOKEN) {
						setIsWrongOtp(true);
						return;
					}
					openFailureDialog();
				}
			);
	};

	return (
		<>
			<ConfirmationDialog
				isOpen={isOpen}
				hideDialog={close}
				loading={loading}
				header={
					isDisconnected
						? translate('sim.management.disable.disconnection')
						: translate('sim.management.disconnect.sim.card')
				}
				dialogBody={
					<>
						<ConditionalRender
							show={!isOTP}
							onTrue={() => {
								return (
									<BodyS>
										{isDisconnected
											? translate('sim.management.disable.disconnection.are.you.sure')
											: translate('sim.management.are.you.sure.you.want.disconnect')}
									</BodyS>
								);
							}}
						/>

						<ConditionalRender
							show={isOTP}
							onTrue={() => {
								return (
									<OtpTab
										otp={otpValue}
										setOtp={setOtpValue}
										sendOtp={requestForOtp}
										otpIncorrect={isWrongOtp}
										inOneInput={false}
										submitOTP={disconnectOrDisable}
									/>
								);
							}}
						/>
					</>
				}
				buttons={[
					{
						text: translate('cancel'),
						onClick: close,
						color: 'tertiary-light',
					},
					{
						text: isDisconnected ? translate('disable') : translate('disconnect'),
						onClick: !isOTP ? requestForOtp : disconnectOrDisable,
						color: 'primary-light',
					},
				]}
			/>
			<SimpleFailureDialog isOpen={showFailureDialog} hideDialog={hideFailureDialog} />
		</>
	);
};

export default DisconnectSimCardDialog;
